import React from 'react';
import styled from 'styled-components';
import { NavLink } from 'react-router-dom';
import Footer from '../components/Footer';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const HeroSection = styled.section`
  text-align: center;
  padding: 60px 0;
  background: linear-gradient(135deg, #27ae60 0%, #2ecc71 100%);
  color: white;
  margin: 0 -20px 60px -20px;
  
  @media (max-width: 768px) {
    padding: 40px 20px;
    margin: 0 -20px 40px -20px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.3rem;
  margin-bottom: 30px;
  opacity: 0.95;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 40px;
  margin-bottom: 80px;
  
  @media (max-width: 768px) {
    margin-bottom: 60px;
  }
`;

const PricingCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  text-align: center;
  position: relative;
  transition: transform 0.3s ease;
  border: ${props => props.featured ? '3px solid #27ae60' : '1px solid #eee'};
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -15px;
  left: 50%;
  transform: translateX(-50%);
  background: #27ae60;
  color: white;
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
`;

const PricingIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 20px;
`;

const PricingTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 10px;
  color: #333;
`;

const PricingPrice = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #27ae60;
  margin-bottom: 10px;
`;

const PricingSubtext = styled.p`
  color: #666;
  margin-bottom: 30px;
  font-size: 1rem;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  text-align: left;
  margin-bottom: 30px;
`;

const FeatureItem = styled.li`
  padding: 8px 0;
  color: #333;
  position: relative;
  padding-left: 25px;
  
  &:before {
    content: '✓';
    position: absolute;
    left: 0;
    color: #27ae60;
    font-weight: bold;
  }
`;

const CTAButton = styled(NavLink)`
  display: inline-block;
  background: ${props => props.featured ? '#27ae60' : '#3498db'};
  color: white;
  padding: 15px 30px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  width: 100%;
  
  &:hover {
    background: ${props => props.featured ? '#229954' : '#2980b9'};
    transform: translateY(-2px);
  }
`;

const ComparisonSection = styled.section`
  margin-bottom: 80px;
  
  @media (max-width: 768px) {
    margin-bottom: 60px;
  }
`;

const ComparisonTable = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    display: block;
    padding: 20px;
    border-bottom: 1px solid #eee;
  }
`;

const TableCell = styled.div`
  padding: 20px;
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    padding: 5px 0;
    &:before {
      content: attr(data-label);
      font-weight: 600;
      width: 120px;
      display: inline-block;
    }
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 40px;
  color: #333;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 30px;
  }
`;

const Pricing = ({ user }) => {
  return (
    <>
      <Container>
        <HeroSection>
          <HeroTitle>Simple, Transparent Pricing</HeroTitle>
          <HeroSubtitle>
            Choose the plan that works for your business. No hidden fees, no contracts.
          </HeroSubtitle>
        </HeroSection>

        <PricingGrid>
          <PricingCard>
            <PricingIcon>🏠</PricingIcon>
            <PricingTitle>Listing Agents</PricingTitle>
            <PricingPrice>FREE</PricingPrice>
            <PricingSubtext>Forever • No limits • No credit card required</PricingSubtext>
            <FeatureList>
              <FeatureItem>Unlimited property listings</FeatureItem>
              <FeatureItem>Professional booking pages</FeatureItem>
              <FeatureItem>Automated tenant compliance</FeatureItem>
              <FeatureItem>24-hour notice automation</FeatureItem>
              <FeatureItem>Calendar integration</FeatureItem>
              <FeatureItem>Lead capture & management</FeatureItem>
              <FeatureItem>Custom availability schedules</FeatureItem>
              <FeatureItem>Unlimited showings</FeatureItem>
            </FeatureList>
            <CTAButton to="/register">Get Started Free</CTAButton>
          </PricingCard>

          <PricingCard featured>
            <PopularBadge>Most Popular</PopularBadge>
            <PricingIcon>🔍</PricingIcon>
            <PricingTitle>Buyer's Agents</PricingTitle>
            <PricingPrice>$29</PricingPrice>
            <PricingSubtext>Per month • Unlimited bookings</PricingSubtext>
            <FeatureList>
              <FeatureItem>Unlimited property showings</FeatureItem>
              <FeatureItem>Professional client experience</FeatureItem>
              <FeatureItem>Access to all listed properties</FeatureItem>
              <FeatureItem>Automated calendar invitations</FeatureItem>
              <FeatureItem>Client communication tools</FeatureItem>
              <FeatureItem>Lead generation opportunities</FeatureItem>
              <FeatureItem>Mobile-optimized booking</FeatureItem>
              <FeatureItem>Priority customer support</FeatureItem>
            </FeatureList>
            <CTAButton to="/register" featured>Start Free Trial</CTAButton>
          </PricingCard>

          <PricingCard>
            <PricingIcon>💼</PricingIcon>
            <PricingTitle>Pay Per Booking</PricingTitle>
            <PricingPrice>$10</PricingPrice>
            <PricingSubtext>Per booking • No monthly commitment</PricingSubtext>
            <FeatureList>
              <FeatureItem>Perfect for occasional use</FeatureItem>
              <FeatureItem>Professional booking experience</FeatureItem>
              <FeatureItem>Access to all properties</FeatureItem>
              <FeatureItem>Calendar invitations</FeatureItem>
              <FeatureItem>Basic communication tools</FeatureItem>
              <FeatureItem>Standard customer support</FeatureItem>
              <FeatureItem>Mobile-optimized booking</FeatureItem>
              <FeatureItem>No monthly fees</FeatureItem>
            </FeatureList>
            <CTAButton to="/register">Get Started</CTAButton>
          </PricingCard>
        </PricingGrid>

        <ComparisonSection>
          <SectionTitle>Feature Comparison</SectionTitle>
          <ComparisonTable>
            <TableHeader>
              <TableCell>Feature</TableCell>
              <TableCell>Pay Per Booking</TableCell>
              <TableCell>Monthly Unlimited</TableCell>
            </TableHeader>
            <TableRow>
              <TableCell data-label="Feature:">Professional booking experience</TableCell>
              <TableCell data-label="Pay Per Booking:">✓</TableCell>
              <TableCell data-label="Monthly Unlimited:">✓</TableCell>
            </TableRow>
            <TableRow>
              <TableCell data-label="Feature:">Calendar integration</TableCell>
              <TableCell data-label="Pay Per Booking:">✓</TableCell>
              <TableCell data-label="Monthly Unlimited:">✓</TableCell>
            </TableRow>
            <TableRow>
              <TableCell data-label="Feature:">Mobile-optimized booking</TableCell>
              <TableCell data-label="Pay Per Booking:">✓</TableCell>
              <TableCell data-label="Monthly Unlimited:">✓</TableCell>
            </TableRow>
            <TableRow>
              <TableCell data-label="Feature:">Lead generation tools</TableCell>
              <TableCell data-label="Pay Per Booking:">Basic</TableCell>
              <TableCell data-label="Monthly Unlimited:">Advanced</TableCell>
            </TableRow>
            <TableRow>
              <TableCell data-label="Feature:">Priority support</TableCell>
              <TableCell data-label="Pay Per Booking:">–</TableCell>
              <TableCell data-label="Monthly Unlimited:">✓</TableCell>
            </TableRow>
            <TableRow>
              <TableCell data-label="Feature:">Advanced analytics</TableCell>
              <TableCell data-label="Pay Per Booking:">–</TableCell>
              <TableCell data-label="Monthly Unlimited:">✓</TableCell>
            </TableRow>
            <TableRow>
              <TableCell data-label="Feature:">Cost for 10 bookings/month</TableCell>
              <TableCell data-label="Pay Per Booking:">$100</TableCell>
              <TableCell data-label="Monthly Unlimited:">$29</TableCell>
            </TableRow>
          </ComparisonTable>
        </ComparisonSection>
      </Container>

      <Footer />
    </>
  );
};

export default Pricing;